import {
    useState, useEffect, useCallback, useMemo,
} from 'react';
import { isEmpty, isEqual } from 'lodash';
import { NSCard, NSDropdown, NSInputNumberStepper } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { useUpdateProForma } from 'queries/ProForma';
import { Tooltip } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import { useQueryClient } from '@tanstack/react-query';
import { isDebtSource } from 'utils/sourceTypeUtils';
import { useGetProFormaSources } from 'views/ProFormaTable/hooks';
import './Sensitivity.scss';
import { useSensitivityStorage } from './hooks/useSensitivityStorage';
import SensitivityTableContainer from './SensitivityTableContainer';
import DeleteSensitivityConfirmationModal from './DeleteSensitivityConfirmationModal/DeleteSensitivityConfirmationModal';
import {
    getSensitivityOptions,
    getSensitivityIdFromType,
    getSensitivityTableId,
    getSensitivityTypeFromId,
    isLoanToCostSensitivity,
    NO_DEBT_SOURCE_TOOLTIP,
} from './constants';
import { useProFormaSocketContext } from '../ProForma/socketContext/ProFormaSocketProvider';

export interface ISensitivityProps {
    hasWriteAccessToProForma: boolean;
    dealId: number;
    proFormaId: number;
}

interface TableConfig {
    id: string;
    type: string;
    tableLabel: string;
}

const Sensitivity = ({
    hasWriteAccessToProForma, dealId, proFormaId,
}: ISensitivityProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const queryClient = useQueryClient();
    const {
        proForma, returnsData, permanentDebtsInfo, sources, sourceTypes,
    } = useProFormaSocketContext();

    const toggleTooltip = () => setShowTooltip(prev => !prev);
    const proFormaStatusId = proForma?.proFormaStatusId;
    const isInProgress = proFormaStatusId === PRO_FORMA_STATUS.IN_PROGRESS;
    const isDraft = proFormaStatusId === PRO_FORMA_STATUS.DRAFT;
    const proFormaInProgress = isInProgress || isDraft;

    const {
        data: proFormaSources = [],
        isSuccess: isProFormaSourcesSuccess,
        isFetchedAfterMount: isProFormaSourcesFetchedAfterMount,
    } = useGetProFormaSources(dealId, proFormaId, { enabled: !isEmpty(proForma) && !proFormaInProgress });
    // Dropping a stored LTC table is destructive: cached rows, a failed fetch, or a row whose source type is unknown must not count as confirmed.
    const sourceTypeIds = new Set(sourceTypes.map(sourceType => sourceType.correlationId));
    const everySourceTypeResolves = proFormaSources.every(source => source.sourceTypeId && sourceTypeIds.has(source.sourceTypeId));
    const isDebtSourceConfirmed = isProFormaSourcesFetchedAfterMount && isProFormaSourcesSuccess && sourceTypes.length > 0 && everySourceTypeResolves;
    const hasDebtSource = proFormaSources.some(source => isDebtSource(source.sourceTypeId, sourceTypes));
    const noDebtSourceConfirmed = isDebtSourceConfirmed && !hasDebtSource;

    const sourcesKey = `${dealId}-${proFormaId}`;
    // A failed refetch keeps the rows but clears isSuccess: rendering and the option use the last known status; dropping needs a confirmed one.
    const [debtSourceKnownKey, setDebtSourceKnownKey] = useState<string | null>(null);
    useEffect(() => {
        if (isDebtSourceConfirmed) setDebtSourceKnownKey(sourcesKey);
    }, [isDebtSourceConfirmed, sourcesKey]);
    const isDebtSourceLastKnown = debtSourceKnownKey === sourcesKey;
    const showLoanToCost = isDebtSourceLastKnown && hasDebtSource;
    const noDebtSourceLastKnown = isDebtSourceLastKnown && !hasDebtSource;

    const sourceRowsSignature = JSON.stringify((sources?.sources ?? []).map(source => [
        source.id, source.sourceTypeId, source.amount, source.name, source.metricType,
    ]));
    useEffect(() => {
        // Socket rows (actuals rows in the project-actuals view) only trigger a refetch; not cancelling keeps opening the section to one request.
        queryClient.invalidateQueries({ queryKey: ['SourcesByProFormaId', dealId, proFormaId] }, { cancelRefetch: false });
    }, [sourceRowsSignature, queryClient, dealId, proFormaId]);

    const defaultTables = useMemo(() => [{
        id: 'main',
        type: 'Exit Capitalization Rate vs Gross Rent',
        tableLabel: 'Exit cap rate vs Gross Rent',
    }], []);

    const {
        saveSensitivity,
        removeSensitivity,
        loadSensitivities,
        clearSensitivities,
    } = useSensitivityStorage(String(proFormaId), proForma?.proFormaModelId);

    const [tables, setTables] = useState<TableConfig[]>(defaultTables);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tableToDelete, setTableToDelete] = useState('');

    const { mutate: updateProForma } = useUpdateProForma();
    const handleUpdateProForma = useCallback((patchData: object) => {
        updateProForma(
            { dealId: Number(dealId), proFormaId: Number(proFormaId), patchData },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['proFormaData'] });
                    queryClient.invalidateQueries({ queryKey: ['ProFormaSensitivityAnalysis'] });
                },
            },
        );
    }, [updateProForma, dealId, proFormaId, queryClient]);

    useEffect(() => {
        const loadedTables = loadSensitivities();
        if (loadedTables.length > 0) {
            setTables(prevTables => {
                // Only update if the tables are actually different
                if (!isEqual(prevTables, loadedTables)) return loadedTables;
                return prevTables;
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proFormaId, proForma?.proFormaModelId]);

    useEffect(() => {
        if (!noDebtSourceConfirmed) return;
        // loadSensitivities may have queued a new table list in this same commit; filter that list, not this render's tables.
        setTables(prevTables => {
            const tablesWithoutLoanToCost = prevTables.filter(table => !isLoanToCostSensitivity(table.type));
            return tablesWithoutLoanToCost.length === prevTables.length ? prevTables : tablesWithoutLoanToCost;
        });
    }, [noDebtSourceConfirmed, tables]);

    useEffect(() => {
        if (proFormaInProgress) {
            clearSensitivities();
            setTables(defaultTables);
        }
    }, [proFormaInProgress, clearSensitivities, defaultTables]);

    // Set initial target IRR if not set and returns data is available
    useEffect(() => {
        if (returnsData?.netLeveredIrr && !proForma?.targetLeveredIrr) {
            handleUpdateProForma({
                targetLeveredIrr: returnsData?.netLeveredIrr,
            });
        }
    }, [returnsData?.netLeveredIrr, proForma?.targetLeveredIrr, handleUpdateProForma]);

    // Save/remove sensitivities when tables change
    useEffect(() => {
        const currentIds = tables.map(table => getSensitivityIdFromType(table.type)).filter(Boolean) as string[];
        const loadedSensitivities = loadSensitivities();
        const savedIds = loadedSensitivities.map(table => getSensitivityIdFromType(table.type)).filter(Boolean) as string[];

        // Add new sensitivities with default increments
        currentIds.forEach(id => {
            if (!savedIds.includes(id)) {
                const sensitivityType = getSensitivityTypeFromId(id);
                const options = getSensitivityOptions(proForma?.proFormaModelId);
                const option = options.find(opt => opt.value === sensitivityType);
                const defaultIncrements = option?.increments || { vertical: 1, horizontal: 0.01 };
                saveSensitivity(id, defaultIncrements.vertical, defaultIncrements.horizontal);
            }
        });

        // Remove sensitivities that are saved but no longer in current tables
        savedIds.forEach(id => {
            if (!currentIds.includes(id)) removeSensitivity(id);
        });
    }, [tables, saveSensitivity, removeSensitivity, loadSensitivities, proForma?.proFormaModelId]);

    const handleSelectSensitivity = (option: IOptionAnyValue) => {
        if (option.value !== 'default') {
            const selectedOption = getSensitivityOptions(proForma?.proFormaModelId).find(opt => opt.value === option.value);
            const sensitivityId = getSensitivityIdFromType(option.value);
            if (selectedOption && sensitivityId) {
                const newTable = {
                    id: getSensitivityTableId(sensitivityId),
                    type: option.value as string,
                    tableLabel: option.label,
                };
                const updatedTables = [...tables, newTable];
                setTables(updatedTables);
            }
        }
    };

    const handleDeleteSensitivityConfirm = (tableId: string) => {
        setTableToDelete(tableId);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteSensitivity = () => {
        const updatedTables = tables.filter(table => table.id !== tableToDelete);
        setTables(updatedTables);
        setTableToDelete('');
        setIsDeleteModalOpen(false);
    };

    const renderedTables = tables.filter(table => showLoanToCost || !isLoanToCostSensitivity(table.type));
    // Built from all tables: an LTC table hidden until its debt source is known must not be addable a second time.
    const selectedTableTypes = new Set(tables.map(table => table.type));
    const hasMultipleLoans = (permanentDebtsInfo?.length ?? 0) > 1;
    const filteredSensitivityOptions = getSensitivityOptions(proForma?.proFormaModelId)
        .filter(option => !selectedTableTypes.has(option.value))
        .map(option => {
            if (isLoanToCostSensitivity(option.value) && !showLoanToCost) {
                return { ...option, disabled: true, tooltip: noDebtSourceLastKnown ? NO_DEBT_SOURCE_TOOLTIP : undefined };
            }
            return hasMultipleLoans && option.verticalLabel === 'Interest rate'
                ? { ...option, label: 'Interest rate (Senior loan) vs LTC' }
                : option;
        });

    return (
        <RenderIf isTrue={!isEmpty(proForma)} isLoading={isEmpty(proForma)}>
            <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma}>
                <NSCard className="NSCard--level-3 d-flex flex-row justify-content-between align-items-center mb-2 p-2">
                    <div>
                        <h4 className="text-white font-weight-bold mb-0 mt-0">Sensitivity analysis</h4>
                        <div>Set your Target Levered IRR to help generate analysis charts</div>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="text-white font-weight-bold mr-2">Target Levered IRR:</span>
                        <NSInputNumberStepper
                            percentage
                            name="targetLeveredIrr"
                            value={proForma?.targetLeveredIrr ? Number(proForma.targetLeveredIrr) : null}
                            min={0}
                            step={0.1}
                            onChange={(value: number) => handleUpdateProForma({
                                targetLeveredIrr: value,
                            })}
                            inputGroupClassName="Sensitivity--input-width"
                        />
                    </div>
                </NSCard>

                {renderedTables.map(table => (
                    <SensitivityTableContainer
                        key={table.id}
                        dealId={dealId}
                        proFormaId={proFormaId}
                        tableConfig={table}
                        onRequestDelete={handleDeleteSensitivityConfirm}
                        proFormaInProgress={proFormaInProgress}
                    />
                ))}

                <RenderIf isTrue={filteredSensitivityOptions.length}>
                    <NSCard>
                        <div className="Sensitivity__add-sensitivity">
                            <div className="Sensitivity__add-sensitivity__content">
                                <div className="Sensitivity__add-sensitivity__title pb-1">Add sensitivity</div>
                                <RenderIf isTrue={proFormaInProgress}>
                                    <Tooltip target="dropdown-tooltip-wrapper" isOpen={showTooltip} toggle={toggleTooltip} autohide={false}>
                                        Your pro forma must be substantially complete to generate sensitivities
                                    </Tooltip>
                                </RenderIf>
                                <div className="Sensitivity__add-sensitivity__dropdown" id="dropdown-tooltip-wrapper">
                                    <NSDropdown
                                        options={filteredSensitivityOptions}
                                        placeholder="Select sensitivity"
                                        handleSelectOption={handleSelectSensitivity}
                                        isFullWidth
                                        disabled={!hasWriteAccessToProForma || proFormaInProgress}
                                    />
                                </div>
                            </div>
                        </div>
                    </NSCard>
                </RenderIf>

                <DeleteSensitivityConfirmationModal
                    isOpen={isDeleteModalOpen}
                    toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)}
                    handleDelete={handleDeleteSensitivity}
                />
            </ReadOnlyWrapper>
        </RenderIf>
    );
};

export default Sensitivity;
